import { useState } from "react";
import {
  Container,
  Title,
  Stack,
  Button,
  Group,
  Badge,
  Table,
  ActionIcon,
  Tooltip,
  Loader,
  Alert,
  Text,
  Modal,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconPlus,
  IconEdit,
  IconStar,
  IconStarFilled,
  IconAlertCircle,
} from "@tabler/icons-react";
import {
  useReportTemplatesAdmin,
  useSetDefaultReportTemplate,
  useDeleteReportTemplate,
} from "../../../hooks/admin/useReportTemplates";
import { ReportTemplate } from "../../../services/admin/reporttemplates.service";
import { ReportTemplateForm } from "../../../components/admin/report-templates/ReportTemplateForm";

export default function ReportTemplatesAdminPage() {
  const [formOpened, { open: openForm, close: closeForm }] =
    useDisclosure(false);
  const [editingTemplate, setEditingTemplate] = useState<ReportTemplate | null>(
    null
  );

  const { data: templates = [], isLoading, error } = useReportTemplatesAdmin();
  const setDefaultMutation = useSetDefaultReportTemplate();
  useDeleteReportTemplate();

  const handleCreate = () => {
    setEditingTemplate(null);
    openForm();
  };

  const handleEdit = (template: ReportTemplate) => {
    setEditingTemplate(template);
    openForm();
  };

  const handleClose = () => {
    setEditingTemplate(null);
    closeForm();
  };

  if (isLoading) {
    return (
      <Container size="xl" pt="xl">
        <Loader />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" pt="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Failed to load report templates.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" pt="xl">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Report Templates</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            Add Template
          </Button>
        </Group>

        <Text size="sm" c="dimmed">
          Manage the report templates used when rendering pathology documents.
          The default template is used when a physician has no template
          assigned.
        </Text>

        <Table striped withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Sections</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {templates.map((template) => {
              const sectionCount =
                template.templateContent?.sections?.length ?? 0;

              return (
                <Table.Tr key={template.id}>
                  <Table.Td>
                    <Group gap={6}>
                      {template.isDefault ? (
                        <Tooltip label="Default template">
                          <IconStarFilled
                            size={14}
                            color="var(--mantine-color-yellow-6)"
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip label="Set as default">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            onClick={() =>
                              setDefaultMutation.mutate(template.id)
                            }
                          >
                            <IconStar size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Text fw={template.isDefault ? 600 : 400}>
                        {template.name}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {template.description || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{sectionCount} sections</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={template.isActive ? "green" : "gray"}
                      variant="light"
                    >
                      {template.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label="Edit">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={() => handleEdit(template)}
                      >
                        <IconEdit size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              );
            })}
            {templates.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text ta="center" c="dimmed" py="md">
                    No report templates found.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Stack>

      <Modal
        opened={formOpened}
        onClose={handleClose}
        title={
          editingTemplate ? "Edit Report Template" : "Create Report Template"
        }
        size="lg"
        scrollAreaComponent={undefined}
      >
        <ReportTemplateForm template={editingTemplate} onClose={handleClose} />
      </Modal>
    </Container>
  );
}
